import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type EventMap, trackEvent } from '../../../lib/analytics';
import { useMixpanel } from '../../../lib/mixpanel';
import { campaignsApi } from '../api/campaignsApi';
import type { CreateCampaignInput, QueryCampaignsParams, UpdateCampaignInput } from '../types';

const campaignKeys = {
  all: ['campaigns'] as const,
  lists: () => [...campaignKeys.all, 'list'] as const,
  list: (params: QueryCampaignsParams) => [...campaignKeys.lists(), params] as const,
  details: () => [...campaignKeys.all, 'detail'] as const,
  detail: (id: string) => [...campaignKeys.details(), id] as const,
  activeCount: (orgId: string) => [...campaignKeys.all, 'active-count', orgId] as const,
  audiencePreview: (id: string) => [...campaignKeys.all, 'audience-preview', id] as const,
};

export function useCampaigns(params: QueryCampaignsParams) {
  return useQuery({
    queryKey: campaignKeys.list(params),
    queryFn: () => campaignsApi.list(params),
    enabled: !!params.organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useCampaign(id: string | undefined, organizationId: string | undefined) {
  return useQuery({
    queryKey: campaignKeys.detail(id ?? ''),
    queryFn: () => campaignsApi.getOne(id ?? '', organizationId ?? ''),
    enabled: !!id && !!organizationId,
    staleTime: 5 * 60_000,
  });
}

function useCampaignMutation<
  TVariables,
  TData = unknown,
  K extends keyof EventMap = keyof EventMap,
>(opts: {
  mutationFn: (vars: TVariables) => Promise<TData>;
  event: K;
  onSuccess: (
    data: TData,
    variables: TVariables,
    invalidate: (key: readonly unknown[]) => void,
  ) => void;
  eventProps?: (vars: TVariables) => EventMap[K];
}) {
  const queryClient = useQueryClient();
  const mp = useMixpanel();
  return useMutation({
    mutationFn: opts.mutationFn,
    onSuccess: (data, variables) => {
      trackEvent(mp, opts.event, opts.eventProps?.(variables));
      const invalidate = (key: readonly unknown[]) =>
        queryClient.invalidateQueries({ queryKey: key });
      opts.onSuccess(data, variables, invalidate);
    },
  });
}

export function useCreateCampaign() {
  return useCampaignMutation({
    mutationFn: (input: CreateCampaignInput) => campaignsApi.create(input),
    event: 'campaign.created',
    onSuccess: (_, variables, invalidate) => {
      invalidate(campaignKeys.lists());
      invalidate(campaignKeys.activeCount(variables.organizationId));
    },
  });
}

export function useUpdateCampaign() {
  return useCampaignMutation({
    mutationFn: ({
      id,
      organizationId,
      input,
    }: {
      id: string;
      organizationId: string;
      input: UpdateCampaignInput;
    }) => campaignsApi.update(id, organizationId, input),
    event: 'campaign.updated',
    onSuccess: (data, _, invalidate) => {
      invalidate(campaignKeys.lists());
      invalidate(campaignKeys.detail(data.id));
    },
  });
}

export function useDeleteCampaign() {
  return useCampaignMutation({
    mutationFn: ({ id, organizationId }: { id: string; organizationId: string }) =>
      campaignsApi.remove(id, organizationId),
    event: 'campaign.deleted',
    onSuccess: (_, variables, invalidate) => {
      invalidate(campaignKeys.lists());
      invalidate(campaignKeys.activeCount(variables.organizationId));
    },
  });
}

export function useActivateCampaign() {
  return useCampaignMutation({
    mutationFn: ({ id, organizationId }: { id: string; organizationId: string }) =>
      campaignsApi.activate(id, organizationId),
    event: 'campaign.activated',
    onSuccess: (data, variables, invalidate) => {
      invalidate(campaignKeys.lists());
      invalidate(campaignKeys.detail(data.id));
      invalidate(campaignKeys.activeCount(variables.organizationId));
    },
  });
}

export function useCompleteCampaign() {
  return useCampaignMutation({
    mutationFn: ({ id, organizationId }: { id: string; organizationId: string }) =>
      campaignsApi.complete(id, organizationId),
    event: 'campaign.completed',
    onSuccess: (data, variables, invalidate) => {
      invalidate(campaignKeys.lists());
      invalidate(campaignKeys.detail(data.id));
      invalidate(campaignKeys.activeCount(variables.organizationId));
    },
  });
}

export function useAddCampaignLeads() {
  return useCampaignMutation({
    mutationFn: ({
      campaignId,
      organizationId,
      leadIds,
    }: {
      campaignId: string;
      organizationId: string;
      leadIds: string[];
    }) => campaignsApi.addLeads(campaignId, organizationId, leadIds),
    event: 'campaign.leads_added',
    eventProps: (vars) => ({ lead_count: vars.leadIds.length }),
    onSuccess: (_, variables, invalidate) => {
      invalidate(campaignKeys.detail(variables.campaignId));
    },
  });
}

export function useRemoveCampaignLeads() {
  return useCampaignMutation({
    mutationFn: ({
      campaignId,
      organizationId,
      leadIds,
    }: {
      campaignId: string;
      organizationId: string;
      leadIds: string[];
    }) => campaignsApi.removeLeads(campaignId, organizationId, leadIds),
    event: 'campaign.leads_removed',
    eventProps: (vars) => ({ lead_count: vars.leadIds.length }),
    onSuccess: (_, variables, invalidate) => {
      invalidate(campaignKeys.detail(variables.campaignId));
    },
  });
}

export function useCampaignActiveCount(organizationId: string | undefined) {
  return useQuery({
    queryKey: campaignKeys.activeCount(organizationId ?? ''),
    queryFn: () => campaignsApi.getActiveCount(organizationId ?? ''),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function usePreviewAudience(
  campaignId: string | undefined,
  organizationId: string | undefined,
) {
  return useQuery({
    queryKey: campaignKeys.audiencePreview(campaignId ?? ''),
    queryFn: () => campaignsApi.previewAudience(campaignId ?? '', organizationId ?? ''),
    enabled: !!campaignId && !!organizationId,
    staleTime: 5 * 60_000,
  });
}
